import { useState, useRef, useLayoutEffect, useEffect } from "react"
import { useSupabaseClient } from "@supabase/auth-helpers-react"
import { toast } from "sonner"
import TYPES_VIEWS from "./types"
import Loading from "./Loading"
import Login from "./Login"
import Register from "./Register"
import ForgotPassword from "./ForgotPassword"
import HeaderPageLogin from "../../../components/ComponentsPageBrad/HeaderPageLogin"
import { useLocation } from "react-router-dom"
import { useTheme } from "../../../contexts/ThemeContext"
import getThemeColors from "../../../utils/getColorTheme"

const NewLogin = () => {
  const location = useLocation()
  const { addTheme } = useTheme()
  const { theme, background } = getThemeColors(addTheme)
  const [currentView, setCurrentView] = useState(TYPES_VIEWS.LOGIN)
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [name, setName] = useState("")
  const [errors, setErrors] = useState([])
  const [isSingup, setIsSingup] = useState(location.state?.isSingup ?? false)

  // Add state for mobile detection with localStorage
  const [isMobile, setIsMobile] = useState(false)
  const [isMobileSmall, setIsMobileSmall] = useState(false)
  const [layoutReady, setLayoutReady] = useState(false)

  const supabaseClient = useSupabaseClient()

  // Add a ref to track resize timeout
  const resizeTimeoutRef = useRef(null)

  useLayoutEffect(() => {
    
  }, []);

  useEffect(() => {
    
  }, [])

  useEffect(() => {
    const handleResize = () => {
      // Clear existing timeout
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }

      // Set layout as not ready during resize
      setLayoutReady(false);

      // Debounce the resize handling
      resizeTimeoutRef.current = setTimeout(() => {
        const mobile = window.innerWidth <= 992;
        const mobileSmall = window.innerWidth <= 480;
        
        setIsMobile(mobile);
        setIsMobileSmall(mobileSmall);
        
        // Store in localStorage for persistence
        localStorage.setItem('isMobile', mobile.toString());
        localStorage.setItem('isMobileSmall', mobileSmall.toString());
        
        // Set layout as ready after state updates
        setTimeout(() => {
          setLayoutReady(true);
        }, 50);
      }, 150); // Debounce delay
    };

    // Initial setup
    const storedIsMobile = localStorage.getItem('isMobile');
    const storedIsMobileSmall = localStorage.getItem('isMobileSmall');
    
    if (storedIsMobile !== null && storedIsMobileSmall !== null) {
      setIsMobile(storedIsMobile === 'true');
      setIsMobileSmall(storedIsMobileSmall === 'true');
    } else {
      const mobile = window.innerWidth <= 992;
      const mobileSmall = window.innerWidth <= 480;
      setIsMobile(mobile);
      setIsMobileSmall(mobileSmall);
      localStorage.setItem('isMobile', mobile.toString());
      localStorage.setItem('isMobileSmall', mobileSmall.toString());
    }

    // Set initial layout ready state
    setTimeout(() => {
      setLayoutReady(true);
    }, 100);

    window.addEventListener("resize", handleResize);
    
    return () => {
      window.removeEventListener("resize", handleResize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, []);

  const login = async () => {
    try {
      setCurrentView(TYPES_VIEWS.LOADING)
      const res = await supabaseClient.auth.signInWithPassword({
        email,
        password,
      })

      if (res?.error) {
        setCurrentView(TYPES_VIEWS.LOGIN)
        if (res?.error?.message === "Invalid login credentials") {
          setErrors([
            {
              field: "email",
              message: "Invalid email. Please try again.",
            },
            {
              field: "password",
              message:
                "Incorrect password. Please try again.",
            },
          ])
        }
      } else {
        const emailConfirmed = res.data.user.user_metadata?.email_verified === true;

        if (!emailConfirmed) {
          const credentials = {
            email,
            password,
            userId: res.data.user.id,
            timestamp: Date.now()
          };

          const encodedCredentials = btoa(JSON.stringify(credentials));
          localStorage.setItem('pendingVerification', encodedCredentials);

          const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/send-verification-email`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              email,
              userId: res.data.user.id
            }),
          });

          if (!response.ok) {
            throw new Error('Failed to send verification email');
          }

          await supabaseClient.auth.signOut();

          window.location.href = `/verification-pending?email=${encodeURIComponent(email)}&userId=${encodeURIComponent(res.data.user.id)}`;
          return;
        }

        window.location.href = "/chat"
      }
    } catch (err) {
      console.error(err)
      setCurrentView(TYPES_VIEWS.LOGIN)
    }
  }

  async function handleRegister({ name, surname, fullname, email, password, terms, newsletter }) {
    setCurrentView(TYPES_VIEWS.LOADING)
    try {
      // Email domain whitelist validation - TEMPORARY
      // To remove this restriction, simply set this constant to false
      const ENFORCE_EMAIL_WHITELIST = process.env.REACT_APP_ENFORCE_EMAIL_WHITELIST === "true";
      const ALLOWED_DOMAINS = process.env.REACT_APP_EMAIL_WHITELIST;
      const ALLOWED_EMAILS = process.env.REACT_APP_EMAIL_ADDRESS_WHITELIST;

      if (ENFORCE_EMAIL_WHITELIST) {
        // Check if the specific email is in the allowlist
        const allowedEmails = ALLOWED_EMAILS ? ALLOWED_EMAILS.replace(/"/g, '').split(',').map(e => e.trim().toLowerCase()) : [];
        
        // Allow if the specific email is whitelisted
        if (allowedEmails.includes(email.toLowerCase())) {
          // Email is explicitly whitelisted, continue with registration
        } else {
          // Check domain whitelist
          const emailDomain = email.split('@')[1]?.toLowerCase();
          const allowedDomains = ALLOWED_DOMAINS ? ALLOWED_DOMAINS.replace(/"/g, '').split(',').map(d => d.trim()) : [];
          
          if (!allowedDomains.includes(emailDomain)) {
            const allowedInfo = allowedDomains.length > 0 
              ? `${allowedDomains.join(', ')} email addresses` 
              : 'whitelisted email addresses';
            toast.error(`Signup is currently restricted to the VIP circle.`);
            setCurrentView(TYPES_VIEWS.LOGIN);
            return;
          }
        }
      }

      
      console.log(`Calling register endpoint`)
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          fullname: fullname,
          name: name,
          surname: surname,
          terms: terms,
          newsletter: newsletter
        }),
        });

      console.log(`register endpoint response status: ${response.status}`);
      console.log(`register endpoint response ok: ${response.ok}`);
      
      const responseData = await response.json();
      console.log(`register endpoint response data:`, responseData);
      console.log(`register endpoint response user:`, responseData?.user);
      console.log(`register endpoint response error:`, responseData?.error);

      if (responseData?.error) {
        toast.error(responseData.error.message || "Failed to sign up. Please try again.")
        setCurrentView(TYPES_VIEWS.LOGIN)
        return
      }

      if (responseData?.user) {
        console.log(`Proceeding to send verification email`);
        const { id } = responseData.user

        const credentials = {
          email,
          password,
          userId: id,
          timestamp: Date.now()
        };

        const encodedCredentials = btoa(JSON.stringify(credentials));
        localStorage.setItem('pendingVerification', encodedCredentials);

        try {
          console.log(`Calling send-verification-email HTTP request`)
          const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/send-verification-email`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              email,
              userId: id,
              fullname: fullname
            }),
            });

            const responseText = await response.text();
            console.log(`send-verification-email HTTP response status: ${response.status}`);
            console.log(`send-verification-email HTTP response headers:`, response.headers);
            console.log(`send-verification-email HTTP response body: ${responseText}`);
            
            if (!response.ok) {
              console.log(`Error - Status: ${response.status}, Body: ${responseText}`);
              throw new Error(`Failed to send verification email (${response.status}): ${responseText}`);
            }

          window.location.href = `/verification-pending?email=${encodeURIComponent(email)}&userId=${encodeURIComponent(id)}`;
          return;
        } catch (emailError) {
          console.error('Error sending verification email:', emailError);
          toast.error("Failed to send verification email. Please try again.");
        }
      }
    } catch (err) {
      toast.error(err.message || "Failed to sign up. Please try again.")
    } finally {
      setCurrentView(TYPES_VIEWS.LOGIN)
      setIsSingup(false)
      setEmail("")
      setPassword("")
      setName("")
    }
  }

  const views = [
    {
      type: TYPES_VIEWS.LOGIN,
      title: "Login",
      content: !isSingup ? (
        <Login
          isSingup={isSingup}
          setIsSingup={setIsSingup}
          errors={errors}
          clickForgotPassword={() => setCurrentView(TYPES_VIEWS.FORGOT_PASSWORD)}
          inputEmailProps={{
            onChange: (e) => setEmail(e.target.value),
            defaultValue: email,
          }}
          inputPasswordProps={{
            onChange: (e) => setPassword(e.target.value),
            defaultValue: password,
          }}
          onSubmit={login}
        />
      ) : (
        <Register
          isSingup={isSingup}
          setIsSingup={setIsSingup}
          errors={errors}
          inputEmailProps={{
            onChange: (e) => setEmail(e.target.value),
            defaultValue: email,
          }}
          inputPasswordProps={{
            onChange: (e) => setPassword(e.target.value),
            defaultValue: password,
          }}
          inputNameProps={{
            onChange: (e) => setName(e.target.value),
            defaultValue: name,
          }}
          handleRegister={handleRegister}
        />
      ),
    },
    {
      type: TYPES_VIEWS.FORGOT_PASSWORD,
      title: "Forgot Password",
      content: <ForgotPassword onClickCancel={() => setCurrentView(TYPES_VIEWS.LOGIN)} />,
    },
    {
      type: TYPES_VIEWS.SET_PASSWORD_FORGOT,
      title: "Set Password",
      content: "",
    },
    {
      type: TYPES_VIEWS.LOADING,
      title: "Loading",
      content: <Loading style={{ margin: "auto" }} />,
    },
  ]

  return (
    <>
      <HeaderPageLogin />
      <section className="section-main-login" style={{ background: background }}>
        <div className="section-main-login__content">
          {currentView === TYPES_VIEWS.LOADING ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100vh",
                width: "100%",
              }}
            >
              <Loading style={{ margin: "auto" }} />
            </div>
          ) : (
            <div
              className="auth-container"
              style={{
                paddingTop: "120px",
                maxHeight: "calc(100vh - 40px)",
                width: "100%",
                boxSizing: "border-box"
              }}
            >
              {currentView !== TYPES_VIEWS.FORGOT_PASSWORD && (
                <div className="auth-toggle">
                  <div className="auth-toggle__wrapper">
                    <button
                      className={`auth-toggle__button ${!isSingup ? "auth-toggle__button--active" : "auth-toggle__button--inactive"}`}
                      onClick={() => setIsSingup(false)}
                      aria-label="Switch to login form"
                      style={{ color: !isSingup ? "#FFFFFF" : theme }}
                    >
                      Login
                    </button>
                    <button
                      className={`auth-toggle__button ${isSingup ? "auth-toggle__button--active" : "auth-toggle__button--inactive"}`}
                      onClick={() => setIsSingup(true)}
                      aria-label="Switch to sign up form"
                      style={{ color: isSingup ? "#FFFFFF" : theme }}
                    >
                      Sign up
                    </button>
                  </div>
                </div>
              )}

              <div className="auth-form">
                {views.find((view) => view.type === currentView)?.content}
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  )
}

export default NewLogin
